import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CheckBadgeIcon,
  CheckIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDirectory } from '../hooks/useDirectory';
import { useMyAccess } from '../hooks/useMyAccess';
import { DEPARTMENT_SPINE } from '../nav/navManifest';
import api from '../services/api';
import {
  SIGNATURE_BRANDS,
  SIGNATURE_LAYOUTS,
  animateSignature,
  layoutSignature,
  loadSignatureAssets,
  renderSlices,
  sliceSignature,
} from './signatureRenderer';

// Email signature builder (/signatures). Anyone signed in fills in their
// details, sees the signature update as they type, and copies it as HTML to
// paste into Outlook — on a laptop, or a narrower version for the phone apps.
//
// The design: a light card, white into soft lilac, with a few fine purple
// lines and soft light streaks gliding along them, passing under the text
// and logos (a white glow shaped like them). Brand column (laptop: left;
// phone: footer): the logo, NANOFIBRE.CO.UK, LinkedIn and Facebook buttons;
// then the name in near-black bold, the job title in purple capitals, a short purple rule, and the
// contact lines with flat purple icons. The card is as tall as its contents.
// Brand purple #711C91, Segoe UI (the corporate identity's font). An
// optional certifications banner (a carousel of the certification logos)
// attaches underneath.
//
// The card is drawn by signatureRenderer.js — on a canvas for the preview,
// and as the copied pictures — so the preview is exactly what pastes. The
// banner is images made by scripts/signature-certs.js, in public/signature
// with the logos and icons, linked from this site so recipients' mail
// clients can load them.
//
// Nano Utilities people get the same signature on that company's navy, with
// lime accents (SIGNATURE_BRANDS in signatureRenderer.js), and Part of
// Nano Fibre where Nano Fibre's says Incorporating Nano Utilities.

// When this code was loaded: part of the copied-signature cache key, so a
// copy made by older code (kept across a live code update) isn't reused.
const LOADED_AT = Date.now();
const ASSETS = `${window.location.origin}/signature`;
// Where the copied signature's uploaded images are served from (the API).
const API_ORIGIN = new URL(process.env.REACT_APP_API_BASE_URL || '/', window.location.origin).origin;
const LAYOUTS = {
  // Laptop: desktop mail window. Phone: fits any phone screen (the card's
  // geometry is in signatureRenderer.js). scale: how big it shows — the
  // design is laid out at `width` and shown `scale` times that (laptop 816
  // wide, about as big as the old company signature); its pictures are drawn
  // at 2x, so they stay sharp.
  laptop: { width: 816, scale: 1 },
  phone: { width: 300, scale: 1 },
};

// Phone numbers: the +44/+27 picker replaces the trunk 0, so the field holds
// only the national number after it (7398 286671, 82 123 4567) — a typed or
// pasted 0, +44 or 0027 is dropped — and it has to be a real number in that
// country's numbering plan to go into the signature.
//   UK (Ofcom): mobiles 7[1-5,7-9] + 8 digits (70 personal and 76 pager
//   numbers aren't mobiles); geographic 1x (10 digits, a few areas 9) and
//   2x (10); non-geographic 3x (10); freephone/special 8x (9-10).
//   SA: 9 digits; mobiles start 6/7/8, geographic 1-5.
const COUNTRIES = {
  uk: {
    code: '44',
    label: 'UK',
    maxDigits: 10,
    valid: { phone: /^(1\d{8,9}|[23]\d{9}|7[1-57-9]\d{8}|8\d{8,9})$/, mobile: /^7[1-57-9]\d{8}$/ },
    example: { phone: '1274 938398', mobile: '7700 900000' },
  },
  za: {
    code: '27',
    label: 'South Africa',
    maxDigits: 9,
    valid: { phone: /^[1-8]\d{8}$/, mobile: /^[678]\d{8}$/ },
    example: { phone: '11 123 4567', mobile: '82 123 4567' },
  },
};

function nationalDigits(country, raw) {
  let digits = String(raw).replace(/\D/g, '');
  const { code, maxDigits } = COUNTRIES[country];
  if (digits.startsWith('00' + code)) digits = digits.slice(2 + code.length);
  else if (/^\s*\+/.test(raw) && digits.startsWith(code)) digits = digits.slice(code.length);
  else if (digits.startsWith(code) && digits.length > maxDigits) digits = digits.slice(code.length);
  return digits.replace(/^0+/, '').slice(0, maxDigits);
}

const group = (digits, sizes) => {
  const parts = [];
  let i = 0;
  for (const size of sizes) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + size));
    i += size;
  }
  if (i < digits.length) parts[parts.length - 1] += digits.slice(i);
  return parts.join(' ');
};

// UK: mobiles 7xxx xxxxxx, London etc. 2x xxxx xxxx, 1x1/11x xxx xxxx,
// other geographic 1xxx xxxxxx (9-digit ones: 1xxxx xxxx), 3xx/8xx xxx xxxx.
// SA: xx xxx xxxx.
function formatNational(country, d) {
  if (country === 'za') return group(d, [2, 3, 4]);
  if (/^7/.test(d)) return group(d, [4, 6]);
  if (/^2/.test(d)) return group(d, [2, 4, 4]);
  if (/^1(\d1|1\d)/.test(d)) return group(d, [3, 3, 4]);
  if (/^1/.test(d) && d.length === 9) return group(d, [5, 4]);
  if (/^1/.test(d)) return group(d, [4, 6]);
  return group(d, [3, 3, 4]);
}

// null unless it's a complete, real number for that country (and a mobile,
// for the mobile field).
function formatPhone(country, digits, kind) {
  const { code, valid } = COUNTRIES[country];
  if (!valid[kind].test(digits)) return null;
  return { display: `+${code} ${formatNational(country, digits)}`, tel: `+${code}${digits}` };
}

// What's wrong with a number that failed formatPhone, in words: the first
// digit, a non-mobile range, or the length.
function phoneProblem(country, digits, kind) {
  const { code, example } = COUNTRIES[country];
  const eg = `e.g. ${example[kind]}`;
  if (country === 'uk') {
    if (kind === 'mobile') {
      if (!/^7/.test(digits)) return `UK mobile numbers start with 7 after +44 (${eg}).`;
      if (/^7[06]/.test(digits)) return `+44 ${digits.slice(0, 2)}… numbers aren't mobile numbers (${eg}).`;
      return `UK mobile numbers have 10 digits after +44 (${eg}).`;
    }
    if (!/^[12378]/.test(digits)) return `UK numbers start with 1, 2, 3, 7 or 8 after +44 (${eg}).`;
    if (/^7[06]/.test(digits)) return `+44 ${digits.slice(0, 2)}… numbers can't be used here (${eg}).`;
    return `That's too short for a UK number (${eg}).`;
  }
  if (kind === 'mobile' && !/^[678]/.test(digits)) return `South African mobile numbers start with 6, 7 or 8 after +${code} (${eg}).`;
  if (!/^[1-8]/.test(digits)) return `South African numbers start with 1 to 8 after +${code} (${eg}).`;
  return `South African numbers have 9 digits after +${code} (${eg}).`;
}

// Where the caret goes in the formatted number: just after the n-th digit.
function caretAfterDigits(formatted, n) {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]) && ++seen === n) return i + 1;
  }
  return formatted.length;
}

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// One row of the copied signature: pictures side by side in a plain block —
// no table. Outlook draws a guide line around every table cell while you
// write, so the signature has no tables at all; font-size and line-height 0
// and no white space between the pictures keep the rows tight, nowrap keeps
// a row on one line, and every picture has a fixed (and minimum) size.
const sigRow = (w, inner) =>
  `<div style="margin:0;padding:0;border:0;font-size:0;line-height:0;white-space:nowrap;width:${w}px;min-width:${w}px;">${inner}</div>`;
const sigImg = (src, w, h, alt) =>
  `<img src="${src}" alt="${alt}" width="${w}" height="${h}" style="display:inline-block;vertical-align:top;margin:0;padding:0;border:0;width:${w}px;min-width:${w}px;height:${h}px;max-width:none;" />`;
const sigLink = (href, inner) => `<a href="${href}" style="text-decoration:none;border:0;">${inner}</a>`;

// The copied signature is pictures: Outlook strips background images and
// recolours or drops HTML text styling when it's pasted (and in dark mode),
// but never touches images. So Copy draws the card (signatureRenderer.js),
// cuts it into slices (each contact line, social button and logo its own,
// wrapped in its link), uploads them as animated GIFs (/api/signatures), and
// copies a plain table of those images, with the certifications banner
// under it.

// What the signature shows: name, title, and the contact lines that are
// filled in (Tel, Cell, Email — told apart by their icons).
function signatureContent(d) {
  const name = [d.firstName, d.surname].map((x) => x.trim()).filter(Boolean).join(' ');
  const rows = [];
  for (const key of ['phone', 'mobile']) {
    const phone = formatPhone(d[`${key}Country`], d[key], key);
    if (phone) rows.push({ icon: key, text: phone.display, href: `tel:${phone.tel}` });
  }
  const email = d.email.trim();
  if (email) rows.push({ icon: 'email', text: email, href: `mailto:${email}` });
  return { name, title: d.title.trim(), rows };
}

// The uploaded slices as rows of pictures (sigRow): one row per band of the
// card (see sliceSignature), each band its pictures side by side, linked
// where they should be, then the certifications banner (belowCard). No
// tables, so Outlook has no cells to draw guide lines around.
function buildImageHtml(slices, id, layoutKey, brandKey) {
  const L = SIGNATURE_LAYOUTS[layoutKey];
  const { scale } = LAYOUTS[layoutKey];
  const px = (v) => Math.round(v * scale);
  const W = px(L.W);
  // Shown at the layout's scale; sizes from the slice's scaled edges, so
  // pieces side by side or stacked add up to the scaled whole.
  const piece = (s) => {
    const tag = sigImg(
      `${API_ORIGIN}/api/signatures/${id}/${s.file}`,
      px(s.x + s.w) - px(s.x),
      px(s.y + s.h) - px(s.y),
      escapeHtml(s.alt)
    );
    return s.href ? sigLink(escapeHtml(s.href), tag) : tag;
  };
  const groups = [...new Set(slices.map((s) => s.group))];
  // On a dark card, the page shows as a line in the hairlines Outlook leaves
  // between pictures. It keeps a table cell's colour on paste (not a block's),
  // so those cards are a table with the card's colour behind every picture.
  // No cell borders: dark mode recolours them, and they push the rows out of
  // line.
  const { seam } = SIGNATURE_BRANDS[brandKey];
  if (seam) {
    const bands = groups.map((g) => slices.filter((s) => s.group === g));
    const columns = Math.max(...bands.map((band) => band.length));
    const cell = (inner, colspan) =>
      `<td${colspan > 1 ? ` colspan="${colspan}"` : ''} bgcolor="${seam}" style="margin:0;padding:0;background-color:${seam};font-size:0;line-height:0;mso-line-height-rule:exactly;">${inner}</td>`;
    const trs = bands.map((band) => `<tr>${band.map((s) => cell(piece(s), band.length > 1 ? 1 : columns)).join('')}</tr>`);
    return `<table width="${W}" cellpadding="0" cellspacing="0" border="0" bgcolor="${seam}" style="width:${W}px;border-collapse:collapse;background-color:${seam};">${trs.join('')}</table>`;
  }
  const rows = groups.map((g) => sigRow(W, slices.filter((s) => s.group === g).map(piece).join('')));
  return `<div style="margin:0;padding:0;border:0;width:${W}px;min-width:${W}px;">${rows.join('')}</div>`;
}

// Bytes -> base64, for the upload.
function toBase64(bytes) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(new Blob([bytes]));
  });
}

// Draw every frame, cut the slices, upload them with the picture that goes
// under the card (so the two are always served from the same place), and
// return the HTML to copy.
async function makeSignatureHtml(d, layoutKey, assets) {
  const lay = layoutSignature(signatureContent(d), layoutKey, d.brand, d.certs);
  const rendered = await renderSlices(lay, assets, sliceSignature(lay));
  const files = {};
  for (const s of rendered) files[s.file] = await toBase64(s.bytes);
  const { data } = await api.post('/api/signatures', { files });
  return buildImageHtml(rendered, data.id, layoutKey, d.brand);
}

// Drawn flags: Windows doesn't render flag emoji (it shows "GB"/"ZA").
function Flag({ country, className = 'h-3.5 w-5' }) {
  const id = useId().replace(/:/g, '');
  if (country === 'za') {
    return (
      <svg viewBox="0 0 9 6" className={`${className} shrink-0 rounded-[2px]`} aria-hidden="true">
        <rect width="9" height="3" fill="#E03C31" />
        <rect y="3" width="9" height="3" fill="#001489" />
        <path d="M0 0L4.5 3H9M0 6L4.5 3" fill="none" stroke="#fff" strokeWidth="2" />
        <path d="M0 0.72L3.42 3L0 5.28Z" fill="#FFB81C" />
        <path d="M0 0L4.5 3H9M0 6L4.5 3" fill="none" stroke="#007749" strokeWidth="1.2" />
        <path d="M0 0.96L3.06 3L0 5.04Z" fill="#000" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 60 30" className={`${className} shrink-0 rounded-[2px]`} aria-hidden="true">
      <clipPath id={`${id}t`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${id}t)`} stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function CountryCodePicker({ value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label} country code: +${COUNTRIES[value].code}`}
        className="flex h-full items-center gap-1.5 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-2.5 text-sm text-gray-700 hover:bg-gray-100 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      >
        <Flag country={value} />
        <span className="tabular-nums">+{COUNTRIES[value].code}</span>
        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {Object.entries(COUNTRIES).map(([key, c]) => (
            <li key={key} role="option" aria-selected={key === value}>
              <button
                type="button"
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-brand-light ${
                  key === value ? 'font-medium text-brand-dark' : 'text-gray-700'
                }`}
              >
                <Flag country={key} />
                <span className="flex-1">{c.label}</span>
                <span className="tabular-nums text-gray-500">+{c.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2.5 text-base sm:py-2 sm:text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

// Name, surname, job title and email are required; Tel and Cell are optional
// (but must be real numbers if given).
const TEXT_FIELDS = [
  { key: 'firstName', label: 'Name', placeholder: 'e.g. Jane', autoComplete: 'given-name', missing: 'Enter your name.' },
  { key: 'surname', label: 'Surname', placeholder: 'e.g. Smith', autoComplete: 'family-name', missing: 'Enter your surname.' },
  { key: 'title', label: 'Job title', placeholder: 'e.g. Operations Manager', autoComplete: 'organization-title', missing: 'Enter your job title.' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Field key -> message, for everything that stops the signature being copied.
function fieldErrors(d) {
  const errors = {};
  for (const f of TEXT_FIELDS) if (!d[f.key].trim()) errors[f.key] = f.missing;
  if (!d.email.trim()) errors.email = 'Enter your email address.';
  else if (!EMAIL_RE.test(d.email.trim())) errors.email = 'Enter a valid email address, e.g. jane.smith@nanofibre.co.uk.';
  for (const key of ['phone', 'mobile']) {
    const country = d[`${key}Country`];
    if (d[key] && !formatPhone(country, d[key], key)) errors[key] = phoneProblem(country, d[key], key);
  }
  return errors;
}

const Required = () => (
  <span className="ml-0.5 text-rose-600" aria-hidden="true">
    *
  </span>
);

// A directory number (the company mobile from the IT asset register, as
// stored: 07…, 08…, +44…, +27…) -> { country, digits } for the Cell field,
// or null if it isn't a valid UK or SA mobile.
function fromDirectoryNumber(raw) {
  const all = String(raw || '').replace(/\D/g, '');
  if (!all) return null;
  const candidates = [];
  if (all.startsWith('44')) candidates.push(['uk', all.slice(2)]);
  if (all.startsWith('27')) candidates.push(['za', all.slice(2)]);
  if (all.startsWith('0') && all.length === 11) candidates.push(['uk', all.slice(1)]);
  if (all.startsWith('0') && all.length === 10) candidates.push(['za', all.slice(1)]);
  candidates.push(['uk', all], ['za', all]);
  const hit = candidates.find(([country, digits]) => formatPhone(country, digits, 'mobile'));
  return hit ? { country: hit[0], digits: hit[1] } : null;
}

// Search the company directory (Microsoft 365 / BrightHR, as on the Directory
// page) and fill the form from the person picked. Everything stays editable.
function PersonPicker({ people, onPick }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // A dropdown of everyone (A-Z) as soon as it's clicked; typing narrows it.
  const everyone = useMemo(
    () => (people || []).filter((p) => p.display_name).sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [people]
  );
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return everyone;
    return everyone.filter((p) => `${p.display_name} ${p.email || ''} ${p.job_title || ''}`.toLowerCase().includes(q));
  }, [everyone, query]);
  const listRef = useRef(null);
  // Keep the keyboard-highlighted row in view as it moves through the list.
  useEffect(() => {
    listRef.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const pick = (p) => {
    onPick(p);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <label htmlFor="sig-person" className="mb-1 block text-sm font-medium text-gray-700">
        Fill in from the directory
      </label>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id="sig-person"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            setOpen(true);
            setHighlight(0);
          }}
          onClick={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (!matches.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => (h + 1) % matches.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => (h - 1 + matches.length) % matches.length);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(matches[highlight]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder="Choose a person, or type to search…"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="sig-person-list"
          className={`${inputClass} cursor-pointer pl-9 pr-9`}
        />
        <ChevronDownIcon
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>
      {open && (
        <ul ref={listRef} id="sig-person-list" role="listbox" className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {matches.map((p, i) => (
            <li key={p.entra_user_id || p.email || p.display_name} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p);
                }}
                className={`block w-full px-3 py-2 text-left ${i === highlight ? 'bg-brand-light' : 'hover:bg-gray-50'}`}
              >
                <span className="block text-sm font-medium text-gray-900">{p.display_name}</span>
                <span className="block truncate text-xs text-gray-500">{[p.job_title, p.email].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          ))}
          {matches.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-400">{people ? 'No one matches that.' : 'Loading the directory…'}</li>
          )}
        </ul>
      )}
      <p className="mt-1 text-xs text-gray-500">Fills in the details below. You can still change anything.</p>
    </div>
  );
}

const PHONE_FIELDS = [
  { key: 'phone', label: 'Tel' },
  { key: 'mobile', label: 'Cell' },
];

function EmailSignatures() {
  const { data: directory } = useDirectory();
  const [details, setDetails] = useState({
    firstName: '',
    surname: '',
    title: '',
    email: '',
    phone: '',
    phoneCountry: 'uk',
    mobile: '',
    mobileCountry: 'uk',
    // The certifications banner under the signature; on unless switched off.
    certs: true,
    // Which company's signature: a key of SIGNATURE_BRANDS.
    brand: 'fibre',
  });
  const [blurred, setBlurred] = useState({});
  // Set by pressing Copy with something missing: shows every error at once.
  const [attempted, setAttempted] = useState(false);
  // The number fields reformat on every keystroke, which would throw the
  // caret to the end; this remembers where it should be (field + how many
  // digits sit before it) and puts it back after the re-render.
  const pendingCaret = useRef(null);
  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (!pending) return;
    pendingCaret.current = null;
    const input = document.getElementById(`sig-${pending.key}`);
    if (input && document.activeElement === input) {
      const pos = caretAfterDigits(input.value, pending.digitsBefore);
      input.setSelectionRange(pos, pos);
    }
  });
  // Phone layout by default on a phone-sized screen.
  const [layout, setLayout] = useState(() => (window.matchMedia?.('(max-width: 639px)').matches ? 'phone' : 'laptop'));
  // idle | preparing | copied | again | incomplete | failed
  const [copyState, setCopyState] = useState('idle');
  // Logo and icon images for drawing the copied signature, loaded once.
  const [assets, setAssets] = useState(null);
  useEffect(() => {
    let live = true;
    loadSignatureAssets(ASSETS).then((a) => live && setAssets(a));
    return () => {
      live = false;
    };
  }, []);
  // The last made signature, so a second Copy (when the browser needed one)
  // doesn't redraw and re-upload the same thing.
  const made = useRef({ key: null, html: null });

  // The preview: the card drawn as it'll be copied, playing on a canvas, and
  // the banner's HTML under it — both shown at the layout's scale.
  const previewLay = useMemo(() => layoutSignature(signatureContent(details), layout, details.brand, details.certs), [details, layout]);
  const previewCanvas = useRef(null);
  useEffect(() => {
    if (!assets || !previewCanvas.current) return undefined;
    return animateSignature(previewCanvas.current, previewLay, assets);
  }, [assets, previewLay]);
  const { scale } = LAYOUTS[layout];

  // Confirming a signature for someone is for full administrators — the
  // same rule the server applies (getMyAccess gives a full admin every
  // department).
  const { data: access } = useMyAccess();
  const isAdmin = Boolean(access?.fullAdmin || DEPARTMENT_SPINE.every((d) => access?.departments?.includes(d.key)));
  // The person picked from the directory (their Microsoft ID and name are
  // stored with the confirmed signature); cleared if the email is changed.
  const [person, setPerson] = useState(null);
  const targetEmail = details.email.trim().toLowerCase();
  const targetValid = EMAIL_RE.test(targetEmail);
  const queryClient = useQueryClient();
  const { data: assigned } = useQuery({
    queryKey: ['signatureAssigned', targetEmail],
    queryFn: async () => (await api.get(`/api/signatures/assigned/${encodeURIComponent(targetEmail)}`)).data.signature,
    enabled: isAdmin && targetValid,
  });
  const [setState, setSetState] = useState('idle'); // idle | saving | saved | failed

  // Picking someone from the directory replaces the fields with their
  // details; a field the directory has nothing for is left as it was.
  const fillFrom = (p) => {
    const [firstName = '', ...rest] = (p.display_name || '').trim().split(/\s+/);
    const cell = fromDirectoryNumber(p.contact_number);
    setPerson({ entraUserId: p.entra_user_id || null, email: (p.email || '').toLowerCase() });
    setSetState('idle');
    setDetails((d) => ({
      ...d,
      firstName: firstName || d.firstName,
      surname: rest.join(' ') || d.surname,
      title: p.job_title || d.title,
      email: p.email || d.email,
      ...(cell ? { mobile: cell.digits, mobileCountry: cell.country } : {}),
    }));
    setBlurred({});
    setAttempted(false);
    setCopyState('idle');
  };

  const set = (key, value) => {
    setDetails((d) => ({ ...d, [key]: value }));
    setCopyState('idle');
    setSetState('idle');
  };

  const errors = fieldErrors(details);
  // Errors show once a field has been left, or after a Copy attempt.
  const shown = (key) => (blurred[key] || attempted) && errors[key];
  const touch = (key) => setBlurred((b) => ({ ...b, [key]: true }));
  const errorClass = (key) => (shown(key) ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500' : '');
  const errorText = (key) =>
    shown(key) ? (
      <p id={`sig-${key}-error`} className="mt-1 text-xs text-rose-600">
        {errors[key]}
      </p>
    ) : null;

  // A phone field edit: keep only the national digits, and remember how many
  // digits were before the caret (minus any 0 / +44 prefix that was dropped)
  // so the caret stays put after reformatting.
  const editPhone = (key, country, raw, caret) => {
    const rawDigits = raw.replace(/\D/g, '');
    const digits = nationalDigits(country, raw);
    const droppedFront = rawDigits.startsWith(digits) ? 0 : rawDigits.length - digits.length;
    const before = raw.slice(0, Math.max(0, caret)).replace(/\D/g, '').length - droppedFront;
    pendingCaret.current = { key, digitsBefore: Math.min(Math.max(0, before), digits.length) };
    set(key, digits);
    setBlurred((b) => ({ ...b, [key]: false }));
  };

  const handleCopy = async () => {
    const firstBad = ['firstName', 'surname', 'title', 'email', 'phone', 'mobile'].find((k) => errors[k]);
    if (firstBad) {
      setAttempted(true);
      setCopyState('incomplete');
      document.getElementById(`sig-${firstBad}`)?.focus();
      return;
    }
    if (!assets || copyState === 'preparing') return;
    const key = JSON.stringify([details, layout, LOADED_AT]);
    const htmlPromise =
      made.current.key === key
        ? Promise.resolve(made.current.html)
        : makeSignatureHtml(details, layout, assets).then((h) => {
            made.current = { key, html: h };
            return h;
          });
    const { name, title, rows } = signatureContent(details);
    const plain = [name, title, ...rows.map((r) => r.text)].filter(Boolean).join('\n');
    setCopyState('preparing');
    try {
      // Handing the clipboard promises keeps the click's permission while the
      // pictures are drawn and uploaded (needed by Safari, fine elsewhere).
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/html': htmlPromise.then((h) => new Blob([h], { type: 'text/html' })),
          'text/plain': htmlPromise.then(() => new Blob([plain], { type: 'text/plain' })),
        }),
      ]);
      setCopyState('copied');
    } catch {
      // Browsers that won't wait for the promise: once it's made, a second
      // click copies it straight away.
      const h = await htmlPromise.catch(() => null);
      setCopyState(h ? 'again' : 'failed');
    }
  };

  // "Set as [Name]'s signature": the admin's confirmation that this is the
  // signature the person uses. Draws and uploads both layouts (laptop and
  // phone, so whatever delivers it can pick), then saves them against the
  // person's email address, replacing any earlier one.
  const handleSetSignature = async () => {
    const firstBad = ['firstName', 'surname', 'title', 'email', 'phone', 'mobile'].find((k) => errors[k]);
    if (firstBad) {
      setAttempted(true);
      setSetState('incomplete');
      document.getElementById(`sig-${firstBad}`)?.focus();
      return;
    }
    if (!assets || setState === 'saving') return;
    setSetState('saving');
    try {
      const [laptop, phone] = [await makeSignatureHtml(details, 'laptop', assets), await makeSignatureHtml(details, 'phone', assets)];
      const name = [details.firstName, details.surname].map((x) => x.trim()).filter(Boolean).join(' ');
      await api.put(`/api/signatures/assigned/${encodeURIComponent(targetEmail)}`, {
        entraUserId: person?.email === targetEmail ? person.entraUserId : null,
        displayName: name,
        details,
        html: { laptop, phone },
      });
      await queryClient.invalidateQueries({ queryKey: ['signatureAssigned', targetEmail] });
      setSetState('saved');
    } catch {
      setSetState('failed');
    }
  };
  const targetName = [details.firstName, details.surname].map((x) => x.trim()).filter(Boolean).join(' ');

  const toggleClass = (active) =>
    `inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium ${
      active ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-600 hover:text-gray-900'
    }`;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Email Signature</h1>
        <p className="mt-1 text-sm text-gray-500">Fill in your details, then copy the signature and paste it into Outlook.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-6">
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">Company</p>
            <div role="radiogroup" aria-label="Company" className="inline-flex rounded-md bg-gray-100 p-1">
              {Object.entries(SIGNATURE_BRANDS).map(([key, brand]) => (
                <button key={key} type="button" role="radio" aria-checked={details.brand === key} onClick={() => set('brand', key)} className={toggleClass(details.brand === key)}>
                  {brand.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-gray-100" />
          <PersonPicker people={directory?.people} onPick={fillFrom} />
          <div className="border-t border-gray-100" />
          {TEXT_FIELDS.map((f) => (
            <div key={f.key}>
              <label htmlFor={`sig-${f.key}`} className="mb-1 block text-sm font-medium text-gray-700">
                {f.label}
                <Required />
              </label>
              <input
                id={`sig-${f.key}`}
                value={details[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                onBlur={() => touch(f.key)}
                placeholder={f.placeholder}
                autoComplete={f.autoComplete}
                required
                aria-invalid={shown(f.key) ? true : undefined}
                aria-describedby={shown(f.key) ? `sig-${f.key}-error` : undefined}
                className={`${inputClass} ${errorClass(f.key)}`}
              />
              {errorText(f.key)}
            </div>
          ))}
          <div>
            <label htmlFor="sig-email" className="mb-1 block text-sm font-medium text-gray-700">
              Email address
              <Required />
            </label>
            <input
              id="sig-email"
              type="email"
              inputMode="email"
              value={details.email}
              onChange={(e) => set('email', e.target.value)}
              onBlur={() => touch('email')}
              placeholder="e.g. jane.smith@nanofibre.co.uk"
              autoComplete="email"
              required
              aria-invalid={shown('email') ? true : undefined}
              aria-describedby={shown('email') ? 'sig-email-error' : undefined}
              className={`${inputClass} ${errorClass('email')}`}
            />
            {errorText('email')}
          </div>
          {PHONE_FIELDS.map((f) => {
            const country = details[`${f.key}Country`];
            const digits = details[f.key];
            const { example } = COUNTRIES[country];
            const invalid = Boolean(shown(f.key));
            return (
              <div key={f.key}>
                <label htmlFor={`sig-${f.key}`} className="mb-1 block text-sm font-medium text-gray-700">
                  {f.label} <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex">
                  <CountryCodePicker
                    label={f.label}
                    value={country}
                    onChange={(v) => {
                      setDetails((d) => ({ ...d, [`${f.key}Country`]: v, [f.key]: d[f.key].slice(0, COUNTRIES[v].maxDigits) }));
                      setCopyState('idle');
                    }}
                  />
                  <input
                    id={`sig-${f.key}`}
                    type="tel"
                    inputMode="tel"
                    value={formatNational(country, digits)}
                    onChange={(e) => editPhone(f.key, country, e.target.value, e.target.selectionStart)}
                    onKeyDown={(e) => {
                      // Backspace / Delete next to a space removes the digit
                      // beyond it (the space itself is formatting).
                      const { value, selectionStart: at, selectionEnd } = e.target;
                      if (at !== selectionEnd) return;
                      if (e.key === 'Backspace' && value[at - 1] === ' ') {
                        e.preventDefault();
                        editPhone(f.key, country, value.slice(0, at - 2) + value.slice(at), at - 2);
                      } else if (e.key === 'Delete' && value[at] === ' ') {
                        e.preventDefault();
                        editPhone(f.key, country, value.slice(0, at) + value.slice(at + 2), at);
                      }
                    }}
                    onBlur={() => setBlurred((b) => ({ ...b, [f.key]: true }))}
                    placeholder={example[f.key]}
                    autoComplete="tel-national"
                    aria-invalid={invalid || undefined}
                    aria-describedby={invalid ? `sig-${f.key}-error` : undefined}
                    className={`${inputClass} min-w-0 rounded-l-none ${invalid ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500' : ''}`}
                  />
                </div>
                {errorText(f.key)}
              </div>
            );
          })}
        </div>

        <div className="min-w-0 space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Preview</p>
            <div className="overflow-x-auto">
              {/* Drawn as it's copied and shown at the size it pastes (its
                  layout's scale), so the preview is exactly what pastes. */}
              <div className="w-max">
                <canvas
                  ref={previewCanvas}
                  className="block"
                  style={{ width: previewLay.L.W * scale, height: previewLay.H * scale }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!assets || copyState === 'preparing'}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-60"
            >
              {copyState === 'copied' ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
              {copyState === 'copied' ? 'Copied' : copyState === 'preparing' ? 'Preparing…' : 'Copy signature'}
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={handleSetSignature}
                disabled={!assets || setState === 'saving'}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-brand bg-white px-3 py-1.5 text-sm font-medium text-brand-dark hover:bg-brand-light disabled:opacity-60"
              >
                <CheckBadgeIcon className="h-4 w-4" />
                {setState === 'saving'
                  ? 'Saving…'
                  : setState === 'saved'
                    ? 'Signature set'
                    : `Set as ${targetName ? `${targetName}'s` : 'their'} signature`}
              </button>
            )}
            <div role="radiogroup" aria-label="Signature layout" className="inline-flex rounded-md bg-gray-100 p-1">
              <button
                type="button"
                role="radio"
                aria-checked={layout === 'laptop'}
                onClick={() => {
                  setLayout('laptop');
                  setCopyState('idle');
                }}
                className={toggleClass(layout === 'laptop')}
              >
                <ComputerDesktopIcon className="h-4 w-4" />
                Laptop
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={layout === 'phone'}
                onClick={() => {
                  setLayout('phone');
                  setCopyState('idle');
                }}
                className={toggleClass(layout === 'phone')}
              >
                <DevicePhoneMobileIcon className="h-4 w-4" />
                Phone
              </button>
            </div>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <button
                type="button"
                role="switch"
                aria-checked={details.certs}
                onClick={() => set('certs', !details.certs)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${details.certs ? 'bg-brand' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${details.certs ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
              Certifications banner
            </label>
            {copyState === 'incomplete' && Object.keys(errors).length > 0 && (
              <p className="text-sm text-rose-600">Fill in the highlighted fields first.</p>
            )}
            {copyState === 'again' && (
              <p className="text-sm text-gray-600">Your signature is ready. Click Copy signature again to copy it.</p>
            )}
            {copyState === 'failed' && (
              <p className="text-sm text-rose-600">Couldn&apos;t make the signature. Check your connection and try again.</p>
            )}
            {setState === 'incomplete' && Object.keys(errors).length > 0 && (
              <p className="text-sm text-rose-600">Fill in the highlighted fields first.</p>
            )}
            {setState === 'failed' && (
              <p className="text-sm text-rose-600">Couldn&apos;t set the signature. Check your connection and try again.</p>
            )}
          </div>
          {isAdmin && targetValid && (
            <p className="text-sm text-gray-500">
              {assigned
                ? `${assigned.display_name || targetEmail}'s current signature was set by ${assigned.set_by_name || assigned.set_by_email || 'an admin'} on ${new Date(assigned.updated_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}.`
                : `No signature has been set for ${targetEmail} yet.`}
            </p>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <p className="mb-2 font-medium text-gray-700">Adding it to Outlook</p>
            {layout === 'laptop' ? (
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  Click <span className="font-medium">Copy signature</span>.
                </li>
                <li>
                  In Outlook, open <span className="font-medium">Settings → Signatures</span> (desktop:{' '}
                  <span className="font-medium">File → Options → Mail → Signatures</span>).
                </li>
                <li>Create a new signature, paste into the editor, and save.</li>
                <li>Set it as the default for new messages and replies.</li>
              </ol>
            ) : (
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  On your phone, tap <span className="font-medium">Copy signature</span>.
                </li>
                <li>
                  In the Outlook app, open <span className="font-medium">Settings → Signature</span>.
                </li>
                <li>Clear the old signature, paste, and go back to save.</li>
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailSignatures;
